import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { ConfirmModal } from "../components/shared/ConfirmModal";
import { TableSkeletonRows } from "../components/ui/Skeleton";
import {
  affiliateApi,
  AffiliateDto,
  ClientReferralSettingsDto,
  CommissionLedgerEntryDto,
  CommissionSummaryDto,
  CreateAffiliateRequest,
  OperatorReferralProgramDto,
  ReferralDto,
  UpdateAffiliateRequest,
} from "../api/affiliateApi";

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n);
}

function tierBadgeClass(tier: string): string {
  const t = tier?.toLowerCase() || "";
  if (t === "external") return "bg-violet-950/80 text-violet-200";
  if (t === "operator") return "bg-blue-950/80 text-blue-200";
  if (t === "client") return "bg-emerald-950/80 text-emerald-200";
  return "bg-slate-800 text-slate-400";
}

const AffiliatePage: React.FC = () => {
  const [affiliates, setAffiliates] = useState<AffiliateDto[]>([]);
  const [referrals, setReferrals] = useState<ReferralDto[]>([]);
  const [ledger, setLedger] = useState<CommissionLedgerEntryDto[]>([]);
  const [summary, setSummary] = useState<CommissionSummaryDto | null>(null);
  const [operatorProgram, setOperatorProgram] = useState<OperatorReferralProgramDto | null>(null);
  const [clientSettings, setClientSettings] = useState<ClientReferralSettingsDto | null>(null);
  const [tierFilter, setTierFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deactivateAffiliateTarget, setDeactivateAffiliateTarget] = useState<AffiliateDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AffiliateDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [calcBusy, setCalcBusy] = useState(false);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [payBusy, setPayBusy] = useState<string | null>(null);

  const [form, setForm] = useState<CreateAffiliateRequest>({
    name: "",
    email: "",
    phone: "",
    tier: "External",
    commissionRate: 20,
    commissionType: "RecurringMonthly",
    oneTimeBonusAmount: undefined,
  });

  const load = useCallback(async () => {
    setError(null);
    try {
      const [a, r, s, l, cs] = await Promise.all([
        affiliateApi.list(tierFilter || undefined),
        affiliateApi.referrals(tierFilter || undefined, statusFilter || undefined),
        affiliateApi.summary(),
        affiliateApi.commissionsList(),
        affiliateApi.clientReferralSettings(),
      ]);
      setAffiliates(a);
      setReferrals(r);
      setSummary(s);
      setLedger(l);
      setClientSettings(cs);
      setOperatorProgram(s.operatorProgram ?? null);
    } catch {
      setError("Could not load affiliates. Try again.");
    } finally {
      setLoading(false);
    }
  }, [tierFilter, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const copyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError("Could not copy to clipboard.");
    }
  };

  const submitAffiliate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const u: UpdateAffiliateRequest = {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          commissionRate: form.commissionRate,
          commissionType: form.commissionType,
          oneTimeBonusAmount: form.oneTimeBonusAmount ?? undefined,
        };
        await affiliateApi.update(editing.id, u);
      } else {
        await affiliateApi.create(form);
      }
      setModalOpen(false);
      setEditing(null);
      setForm({
        name: "",
        email: "",
        phone: "",
        tier: "External",
        commissionRate: 20,
        commissionType: "RecurringMonthly",
        oneTimeBonusAmount: undefined,
      });
      await load();
    } catch {
      setError(editing ? "Could not update affiliate." : "Could not create affiliate.");
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      email: "",
      phone: "",
      tier: "External",
      commissionRate: 20,
      commissionType: "RecurringMonthly",
      oneTimeBonusAmount: undefined,
    });
    setModalOpen(true);
  };

  const openEdit = (x: AffiliateDto) => {
    setEditing(x);
    setForm({
      name: x.name,
      email: x.email,
      phone: x.phone,
      tier: x.tier,
      commissionRate: x.commissionRate,
      commissionType: x.commissionType,
      oneTimeBonusAmount: x.oneTimeBonusAmount ?? undefined,
    });
    setModalOpen(true);
  };

  const runCalculate = async () => {
    setCalcBusy(true);
    setError(null);
    try {
      await affiliateApi.calculateCommissions();
      await load();
    } catch {
      setError("Commission calculation failed.");
    } finally {
      setCalcBusy(false);
    }
  };

  const runLedgerCalc = async () => {
    setLedgerBusy(true);
    setError(null);
    try {
      await affiliateApi.commissionsCalculate();
      await load();
    } catch {
      setError("Ledger calculation failed.");
    } finally {
      setLedgerBusy(false);
    }
  };

  const runLedgerPayout = async () => {
    setLedgerBusy(true);
    setError(null);
    try {
      await affiliateApi.commissionsPayout();
      await load();
    } catch {
      setError("Batch payout failed.");
    } finally {
      setLedgerBusy(false);
    }
  };

  const exportLedgerCsv = () => {
    const headers = ["Date", "Affiliate", "Amount", "Type", "Status", "Description"];
    const rows = ledger.map((e) => [
      e.earnedAt,
      e.affiliateName,
      String(e.amount),
      e.type,
      e.status,
      (e.description ?? "").replace(/"/g, '""'),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "commissions.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const markPaid = async (id: string) => {
    setPayBusy(id);
    setError(null);
    try {
      await affiliateApi.markPaid(id);
      await load();
    } catch {
      setError("Could not mark referral as paid.");
    } finally {
      setPayBusy(null);
    }
  };

  const saveClientSettings = async () => {
    if (!clientSettings) return;
    setSaving(true);
    setError(null);
    try {
      const u = await affiliateApi.updateClientReferralSettings({
        clientReferralsEnabled: clientSettings.clientReferralsEnabled,
        clientReferrerRewardAmount: clientSettings.clientReferrerRewardAmount,
        clientReferrerRewardType: clientSettings.clientReferrerRewardType,
        clientNewClientRewardAmount: clientSettings.clientNewClientRewardAmount,
        clientNewClientRewardType: clientSettings.clientNewClientRewardType,
      });
      setClientSettings(u);
    } catch {
      setError("Could not save client referral settings.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivateAffiliate = async () => {
    const x = deactivateAffiliateTarget;
    setDeactivateAffiliateTarget(null);
    if (!x) {
      return;
    }
    try {
      await affiliateApi.update(x.id, { status: "Inactive" });
      await load();
    } catch {
      setError("Update failed.");
    }
  };

  const opLink = operatorProgram?.referralLink || "";

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Affiliates & referrals</h1>
          <p className="mt-1 text-sm text-slate-400">
            Three-tier program: external partners, operator-to-operator growth, and client referrals.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm text-red-200">{error}</div>
        )}

        {summary && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {[
              {
                label: "Active affiliates",
                value: String(summary.activeAffiliateCount),
                sub: `Ext ${summary.tierExternalCount} · Op ${summary.tierOperatorCount} · Client ${summary.tierClientCount}`,
              },
              { label: "Pending referrals", value: String(summary.pendingReferralCount), sub: "Awaiting conversion" },
              {
                label: "Pipeline (ledger pending)",
                value: formatMoney(summary.pendingPipelineAmount),
                sub: `${summary.convertedReferralCount} converted`,
              },
              { label: "Total paid out", value: formatMoney(summary.totalPaidOut), sub: "Commissions + legacy" },
              {
                label: "Your referral link",
                value: operatorProgram ? operatorProgram.referralCode : "—",
                sub: "Tier 2 · copy full URL below",
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 shadow-inner shadow-black/20"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{card.label}</p>
                <p className="mt-2 text-xl font-semibold text-white">{card.value}</p>
                <p className="mt-1 text-xs text-slate-500">{card.sub}</p>
              </div>
            ))}
          </div>
        )}

        {operatorProgram && (
          <section className="rounded-xl border border-blue-900/40 bg-slate-900/60 p-6">
            <h2 className="text-lg font-semibold text-white">Your referral program (Tier 2)</h2>
            <p className="mt-1 text-sm text-slate-400">
              Share IntelliWarrior with other operators. 15% recurring + $50 signup bonus on activation (configured on
              the affiliate record).
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <input
                readOnly
                className="min-w-[240px] flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-200"
                value={opLink}
              />
              <button
                type="button"
                onClick={() => void copyText(opLink, "op")}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
              >
                {copied === "op" ? "Copied!" : "Copy link"}
              </button>
            </div>
            <div className="mt-4 grid gap-3 text-sm text-slate-400 sm:grid-cols-3">
              <p>Referrals: {operatorProgram.totalReferrals}</p>
              <p>Pending credits: {formatMoney(operatorProgram.pendingBalance)}</p>
              <p>
                Free-month milestone: {operatorProgram.milestoneProgress}/{operatorProgram.milestoneTarget} conversions
              </p>
            </div>
            <div className="mt-3 h-2 max-w-md overflow-hidden rounded-full bg-slate-800">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{
                  width: `${Math.min(100, (operatorProgram.milestoneProgress / operatorProgram.milestoneTarget) * 100)}%`,
                }}
              />
            </div>
          </section>
        )}

        {clientSettings && (
          <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold text-white">Client referrals (Tier 3)</h2>
            <p className="mt-1 text-sm text-slate-500">{clientSettings.clientPreviewSummary}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={clientSettings.clientReferralsEnabled}
                  onChange={(e) => setClientSettings({ ...clientSettings, clientReferralsEnabled: e.target.checked })}
                />
                Enable client referrals in portal
              </label>
              <div>
                <label className="text-xs text-slate-500">Referrer reward ($)</label>
                <input
                  type="number"
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                  value={clientSettings.clientReferrerRewardAmount}
                  onChange={(e) =>
                    setClientSettings({ ...clientSettings, clientReferrerRewardAmount: Number(e.target.value) })
                  }
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Referrer reward type</label>
                <select
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                  value={clientSettings.clientReferrerRewardType}
                  onChange={(e) =>
                    setClientSettings({ ...clientSettings, clientReferrerRewardType: e.target.value })
                  }
                >
                  <option value="Credit">Credit</option>
                  <option value="FreeMonth">Free month</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">New client reward ($)</label>
                <input
                  type="number"
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                  value={clientSettings.clientNewClientRewardAmount}
                  onChange={(e) =>
                    setClientSettings({ ...clientSettings, clientNewClientRewardAmount: Number(e.target.value) })
                  }
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">New client reward type</label>
                <select
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                  value={clientSettings.clientNewClientRewardType}
                  onChange={(e) =>
                    setClientSettings({ ...clientSettings, clientNewClientRewardType: e.target.value })
                  }
                >
                  <option value="Credit">Credit</option>
                  <option value="FreeMonth">Free month</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={() => void saveClientSettings()}
              className="mt-4 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              Save Tier 3 settings
            </button>
          </section>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <button
            type="button"
            onClick={() => openCreate()}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 sm:w-auto"
          >
            Add affiliate
          </button>
          <button
            type="button"
            disabled={calcBusy}
            onClick={() => void runCalculate()}
            className="w-full rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-50 sm:w-auto"
          >
            {calcBusy ? "Calculating…" : "Calculate commissions (legacy)"}
          </button>
        </div>

        <section className="rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="flex flex-col gap-3 border-b border-slate-800 px-5 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white">Affiliate management</h2>
              <p className="text-sm text-slate-500">Tier 1 external partners and managed affiliates.</p>
            </div>
            <select
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-white sm:w-auto sm:min-w-[10rem]"
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
            >
              <option value="">All tiers</option>
              <option value="External">External</option>
              <option value="Operator">Operator</option>
              <option value="Client">Client</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <p className="px-5 py-8 text-sm text-slate-500">Loading…</p>
            ) : affiliates.length === 0 ? (
              <p className="px-5 py-8 text-sm text-slate-500">No affiliates match this filter.</p>
            ) : (
              <table className="w-full min-w-[960px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium">Tier</th>
                    <th className="px-5 py-3 font-medium">Code</th>
                    <th className="px-5 py-3 font-medium">Refs</th>
                    <th className="px-5 py-3 font-medium">Earned</th>
                    <th className="px-5 py-3 font-medium">Pending</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80">
                  {affiliates.map((a) => (
                    <tr key={a.id} className="text-slate-300">
                      <td className="px-5 py-3 font-medium text-white">{a.name}</td>
                      <td className="px-5 py-3 text-slate-400">{a.email}</td>
                      <td className="px-5 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${tierBadgeClass(a.tier)}`}>{a.tier}</span>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-blue-300">{a.referralCode}</td>
                      <td className="px-5 py-3">{a.totalReferrals}</td>
                      <td className="px-5 py-3">{formatMoney(a.totalEarned)}</td>
                      <td className="px-5 py-3">{formatMoney(a.pendingBalance)}</td>
                      <td className="px-5 py-3 text-xs">{a.status}</td>
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          onClick={() => void copyText(a.referralLink || "", a.id)}
                          className="mr-2 text-xs text-blue-400 hover:text-blue-300"
                        >
                          {copied === a.id ? "Copied" : "Copy"}
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          className="mr-2 text-xs text-slate-300 hover:text-white"
                        >
                          Edit
                        </button>
                        {a.tier !== "Operator" && (
                          <button
                            type="button"
                            onClick={() => setDeactivateAffiliateTarget(a)}
                            className="text-xs text-red-400 hover:text-red-300"
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 px-5 py-4">
            <h2 className="text-lg font-semibold text-white">Referral tracking</h2>
            <div className="flex flex-wrap gap-2">
              <select
                className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="Pending">Pending</option>
                <option value="Active">Active</option>
                <option value="Converted">Converted</option>
                <option value="Enrolled">Enrolled</option>
                <option value="Paid">Paid</option>
                <option value="Churned">Churned</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="px-5 py-6">
                <TableSkeletonRows rows={4} cols={8} />
              </div>
            ) : referrals.length === 0 ? (
              <p className="px-5 py-8 text-sm text-slate-500">No referrals for this view.</p>
            ) : (
              <table className="w-full min-w-[1000px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="px-5 py-3 font-medium">Referred</th>
                    <th className="px-5 py-3 font-medium">By (affiliate)</th>
                    <th className="px-5 py-3 font-medium">Tier</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Commission</th>
                    <th className="px-5 py-3 font-medium">Date</th>
                    <th className="px-5 py-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80">
                  {referrals.map((r) => (
                    <tr key={r.id} className="text-slate-300">
                      <td className="px-5 py-3">
                        <p className="font-medium text-white">{r.clientName || r.referredName}</p>
                        <p className="text-xs text-slate-500">{r.clientEmail || r.referredEmail}</p>
                      </td>
                      <td className="px-5 py-3">{r.affiliateName}</td>
                      <td className="px-5 py-3 text-xs">{r.affiliateTier}</td>
                      <td className="px-5 py-3 text-xs">{r.referredEntityType}</td>
                      <td className="px-5 py-3 text-xs">{r.status}</td>
                      <td className="px-5 py-3">{formatMoney(r.commissionAmount)}</td>
                      <td className="px-5 py-3 text-xs text-slate-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                      <td className="px-5 py-3">
                        {r.status === "Enrolled" && (
                          <button
                            type="button"
                            disabled={payBusy === r.id}
                            onClick={() => void markPaid(r.id)}
                            className="text-xs font-medium text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
                          >
                            {payBusy === r.id ? "…" : "Mark paid"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Commission ledger</h2>
              <p className="text-sm text-slate-500">Pending → approve → batch payout.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={ledgerBusy}
                onClick={() => void runLedgerCalc()}
                className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50"
              >
                Calculate
              </button>
              <button
                type="button"
                disabled={ledgerBusy}
                onClick={() => void runLedgerPayout()}
                className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                Process payouts
              </button>
              <button
                type="button"
                onClick={() => exportLedgerCsv()}
                className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
              >
                Export CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {ledger.length === 0 ? (
              <p className="px-5 py-8 text-sm text-slate-500">No ledger rows yet. Run calculate to generate entries.</p>
            ) : (
              <table className="w-full min-w-[800px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="px-5 py-3 font-medium">Date</th>
                    <th className="px-5 py-3 font-medium">Affiliate</th>
                    <th className="px-5 py-3 font-medium">Amount</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Approve</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((e) => (
                    <tr key={e.id} className="border-b border-slate-800/80 text-slate-300">
                      <td className="px-5 py-3 text-xs">{new Date(e.earnedAt).toLocaleString()}</td>
                      <td className="px-5 py-3">{e.affiliateName}</td>
                      <td className="px-5 py-3">{formatMoney(e.amount)}</td>
                      <td className="px-5 py-3 text-xs">{e.type}</td>
                      <td className="px-5 py-3 text-xs">{e.status}</td>
                      <td className="px-5 py-3">
                        {e.status === "Pending" && (
                          <button
                            type="button"
                            onClick={() => affiliateApi.commissionsApprove(e.id).then(() => load())}
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-white">{editing ? "Edit affiliate" : "Add affiliate"}</h3>
              <form onSubmit={(e) => void submitAffiliate(e)} className="mt-6 space-y-4">
                {!editing && (
                  <div>
                    <label className="block text-xs font-medium text-slate-400">Tier</label>
                    <select
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                      value={form.tier}
                      onChange={(e) => {
                        const t = e.target.value;
                        const rate = t === "External" ? 20 : t === "Operator" ? 15 : 0;
                        const bonus = t === "Operator" ? 50 : undefined;
                        setForm((f) => ({ ...f, tier: t, commissionRate: rate, oneTimeBonusAmount: bonus }));
                      }}
                    >
                      <option value="External">External (20% recurring default)</option>
                      <option value="Operator">Operator (15% + bonus default)</option>
                      <option value="Client">Client (credit — usually auto)</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-slate-400">Name</label>
                  <input
                    required
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400">Email</label>
                  <input
                    required
                    type="email"
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400">Phone</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400">Commission rate (%)</label>
                  <input
                    required
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                    value={form.commissionRate}
                    onChange={(e) => setForm((f) => ({ ...f, commissionRate: Number(e.target.value) }))}
                  />
                </div>
                {(form.tier === "Operator" || form.tier === "External") && (
                  <div>
                    <label className="block text-xs font-medium text-slate-400">One-time bonus ($)</label>
                    <input
                      type="number"
                      min={0}
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                      value={form.oneTimeBonusAmount ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          oneTimeBonusAmount: e.target.value === "" ? undefined : Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                )}
                <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(false);
                      setEditing(null);
                    }}
                    className="w-full rounded-lg px-4 py-2 text-sm text-slate-400 hover:bg-slate-800 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 sm:w-auto"
                  >
                    {saving ? "Saving…" : editing ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmModal
          isOpen={deactivateAffiliateTarget !== null}
          title="Deactivate Affiliate"
          message={
            deactivateAffiliateTarget
              ? `This will mark "${deactivateAffiliateTarget.name}" inactive. They will stop earning new commissions until reactivated.`
              : ""
          }
          confirmLabel="Deactivate"
          confirmVariant="warning"
          onConfirm={() => void confirmDeactivateAffiliate()}
          onCancel={() => setDeactivateAffiliateTarget(null)}
        />
      </div>
    </DashboardLayout>
  );
};

export default AffiliatePage;
